import * as Sentry from '@sentry/nextjs';

import { GetMeQuery } from '@client/generated-graphql';
import { useQueryClient } from '@tanstack/react-query';
import fetchWithAuth from './fetch-with-auth.client';
import intercept from './intercept';
import adminInterceptor from './interceptors/admin-interceptor';
import disconnectedTenantInterceptor from './interceptors/disconnected-tenant-interceptor';
import useGetPromiseDataObj from './use-get-promise-data-obj';

interface GraphQLResponse<T = any> {
  data?: T;
  errors?: Array<{
    message: string;
    locations?: Array<{ line: number; column: number }>;
    path?: Array<string | number>;
  }>;
}

export async function fetcher<
  TData = any,
  TVariables extends { [key: string]: any } | undefined = undefined,
>({
  query,
  variables,
}: {
  query: string;
  variables?: TVariables;
}): Promise<TData> {
  try {
    const response: GraphQLResponse<TData> = await fetchWithAuth<TData>(
      query,
      variables,
    );
    if (response?.errors && response?.errors.length > 0) {
      const error = new Error(response.errors[0].message || 'GraphQL Error');
      (error as any).graphqlErrors = response?.errors || [];
      throw error;
    }

    if (!response.data) {
      throw new Error('Data is missing from the response.');
    }
    intercept({ res: response }).process(disconnectedTenantInterceptor);
    return response.data;
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
    throw error;
  }
}

export const useMyQuery = <
  TData = any,
  TVariables extends { [key: string]: any } | undefined = undefined,
>(
  queryDocument: string,
) => {
  const queryClient = useQueryClient();
  const prefetchedPromiseDataObj = useGetPromiseDataObj({
    query: queryDocument,
  });

  if (prefetchedPromiseDataObj?.promise) {
    return () => prefetchedPromiseDataObj.promise;
  }

  // we can return prefetched promise here if we catch them
  const { getMe: me }: GetMeQuery =
    queryClient.getQueryData(['getMe']) || ({} as GetMeQuery['getMe']);
  const isAdmin = Boolean(
    me?.isAdminMode !== null ? me?.isAdminMode : me?.isAdmin,
  );
  return (variables?: TVariables): Promise<TData> => {
    const req = intercept({ query: queryDocument, variables }, { isAdmin })
      .process(adminInterceptor)
      .valueOf();
    if (!req?.query) {
      return { data: null };
    }
    return fetcher<TData, TVariables>({
      query: queryDocument,
      variables,
    });
  };
};
